// src/components/Dashboard/ExistingPatientSearch.jsx
import { useState, useEffect } from "react";
import { API_BASE } from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";
import PatientPreview from "./PatientPreview";
import { FaList, FaSearch, FaEye, FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

export default function ExistingPatientSearch({ onSelect, onEdit }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewPatient, setPreviewPatient] = useState(null);
  const [showAllPatients, setShowAllPatients] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [allPatientsLoading, setAllPatientsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetch(`${API_BASE}/patients/recent`, {
      headers: getAuthHeaders()
    })
      .then((res) => res.json())
      .then((data) => setResults(data))
      .catch(console.error);
  }, [getAuthHeaders]);

  const handleSearch = () => {
    if (!query.trim()) return;
    setLoading(true);
    fetch(`${API_BASE}/patients/search?query=${encodeURIComponent(query)}`, {
      headers: getAuthHeaders()
    })
      .then((res) => res.json())
      .then((data) => setResults(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const fetchAllPatients = async () => {
    try {
      setAllPatientsLoading(true);
      const response = await fetch(`${API_BASE}/patients/all`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to fetch patients');
      }
      const data = await response.json();
      setAllPatients(data);
    } catch (error) {
      console.error('Error fetching all patients:', error);
      alert('Error loading patients. Please try again.');
    } finally {
      setAllPatientsLoading(false);
    }
  };

  const handleDelete = async (id, patientName) => {
    const password = prompt(`Enter password to delete ${patientName}:`);
    if (!password) return;

    try {
      const res = await fetch(`${API_BASE}/patients/delete/${id}`, {
        method: "DELETE",
        headers: { 
          ...getAuthHeaders(),
          "Content-Type": "application/json" 
        },
        body: JSON.stringify({ password }),
      });
      const body = await res.json();
      if (res.ok) {
        alert("Patient deleted successfully");
        // Refresh the appropriate list
        if (showAllPatients) {
          fetchAllPatients();
        } else {
          // Refresh recent patients
          fetch(`${API_BASE}/patients/recent`, {
            headers: getAuthHeaders()
          })
            .then((res) => res.json())
            .then((data) => setResults(data))
            .catch(console.error);
        }
      } else {
        alert("Delete failed: " + body.error);
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting patient");
    }
  };

  const handleSelect = (patient) => {
    setPreviewPatient(patient);
  };

  const handleEdit = (patient) => {
    setPreviewPatient(null);
    onEdit(patient);
  };

  const handlePatientClick = (patient) => {
    onEdit(patient); // Direct edit mode
  };

  const handleViewAllPatients = () => {
    setShowAllPatients(true);
    fetchAllPatients();
  };

  const handleBackToSearch = () => {
    setShowAllPatients(false);
    setQuery("");
    setResults([]);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortPatients = (patients) => {
    return [...patients].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'created_at' || sortConfig.key === 'date') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle numeric sorting
      if (typeof aValue === 'number') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return "—";
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-gray-400" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="text-blue-600" /> : <FaSortDown className="text-blue-600" />;
  };

  if (previewPatient) {
    return (
      <PatientPreview
        patient={previewPatient}
        onEdit={handleEdit}
        onClose={() => setPreviewPatient(null)}
      />
    );
  }

  if (showAllPatients) {
    const sortedPatients = sortPatients(allPatients);

    return (
      <div className="max-w-6xl mx-auto mt-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-0">
              <FaList className="text-2xl text-green-600" />
              <h2 className="text-2xl font-bold text-gray-800">
                All Registered Patients ({sortedPatients.length})
              </h2>
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleBackToSearch}
                className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
                title="Back to search"
              >
                ← Back to Search
              </button>
              <button
                onClick={fetchAllPatients}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                title="Refresh list"
              >
                🔄 Refresh
              </button>
            </div>
          </div>

          {allPatientsLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading all patients...</p>
              </div>
            </div>
          ) : sortedPatients.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No patients registered yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-green-50 border-b border-green-200">
                    <th className="text-left py-3 px-4 font-semibold text-green-800">
                      <button 
                        onClick={() => handleSort('id')}
                        className="flex items-center gap-1 hover:text-green-900"
                      >
                        S.No {getSortIcon('id')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-green-800">
                      <button 
                        onClick={() => handleSort('registration_number')}
                        className="flex items-center gap-1 hover:text-green-900"
                      >
                        Registration No. {getSortIcon('registration_number')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-green-800">
                      <button 
                        onClick={() => handleSort('firstname')}
                        className="flex items-center gap-1 hover:text-green-900"
                      >
                        Patient Name {getSortIcon('firstname')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-green-800">
                      <button 
                        onClick={() => handleSort('age')}
                        className="flex items-center gap-1 hover:text-green-900"
                      >
                        Age/Gender {getSortIcon('age')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-green-800">Contact</th>
                    <th className="text-left py-3 px-4 font-semibold text-green-800">
                      <button 
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-1 hover:text-green-900"
                      >
                        Entry Date {getSortIcon('created_at')}
                      </button>
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-green-800">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPatients.map((patient, index) => (
                    <tr 
                      key={patient.id} 
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 text-gray-600 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handlePatientClick(patient)}
                          className="text-green-700 font-semibold hover:text-green-900 hover:underline cursor-pointer"
                        >
                          {patient.registration_number}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handlePatientClick(patient)}
                          className="text-gray-800 font-medium hover:text-green-700 hover:underline cursor-pointer"
                        >
                          {patient.firstname} {patient.lastname}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {patient.age} / {patient.gender}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        <div className="text-sm">
                          <div>{patient.mobilenumber || "—"}</div>
                          <div className="text-gray-500">{patient.email || "—"}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {formatDate(patient.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSelect(patient)}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                            title="View Patient"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handlePatientClick(patient)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="Edit Patient"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(patient.id, `${patient.firstname} ${patient.lastname}`)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Delete Patient"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!allPatientsLoading && sortedPatients.length > 0 && (
            <div className="mt-4 text-center">
              <p className="text-gray-600 text-sm">
                💡 Click on patient name or registration number to edit • Click the eye icon to view details • Click column headers to sort
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <FaSearch className="text-2xl text-green-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              Search & Manage Patients
            </h2>
          </div>
          
          <button
            onClick={handleViewAllPatients}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
          >
            <FaList />
            View All Patients
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, mobile or reg no..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 px-4 py-2 border rounded-md shadow-sm focus:ring-2 focus:ring-green-500"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            🔍 Search
          </button>
        </div>

        {loading && <p className="text-gray-500">Searching...</p>}

        {results.length > 0 && (
          <ul className="border rounded divide-y">
            {results.map((p) => (
              <li key={p.id} className="p-3 flex justify-between hover:bg-gray-50">
                <div className="flex-1">
                  <div className="font-medium text-green-700">
                    <button
                      onClick={() => handlePatientClick(p)}
                      className="hover:text-green-900 hover:underline cursor-pointer"
                    >
                      {p.registration_number}
                    </button>
                    {" – "}
                    <button
                      onClick={() => handlePatientClick(p)}
                      className="hover:text-green-900 hover:underline cursor-pointer"
                    >
                      {p.firstname} {p.lastname}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    {p.email} | {p.mobilenumber}
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <button onClick={() => handleSelect(p)} className="text-blue-600 hover:underline text-sm">👁️ View</button>
                  <button onClick={() => handlePatientClick(p)} className="text-green-600 hover:underline text-sm">✏️ Edit</button>
                  <button onClick={() => handleDelete(p.id, `${p.firstname} ${p.lastname}`)} className="text-red-600 hover:underline text-sm">🗑️ Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && query && results.length === 0 && (
          <p className="text-red-500">No matching patients found.</p>
        )}

        {!loading && !query && results.length > 0 && (
          <div className="mt-4 text-center">
            <p className="text-gray-600 text-sm">
              💡 Click on patient name or registration number to edit directly
            </p>
          </div>
        )}

        {!loading && !query && results.length === 0 && (
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-lg mb-4">No recent patients found</p>
            <button
              onClick={handleViewAllPatients}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              View All Patients
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
